import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useExtendedState from '../../hooks/useExtendedState';
import TextField from '../fields/TextField';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import SaveButton from '../buttons/SaveButton';
import { ALL_PRODUCTS_QUERY_NAME, CREATE_PRODUCT } from '../../graphql/products';
import SelectField from '../fields/SelectField';
import { productPropertiesType } from '../../types';
import IconField from '../fields/IconField';
import ColorField from '../fields/ColorField';
import { getAllScopes, ETS1 } from '../../utils/Scopes';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    isinCodeValidator,
    PRODUCT_ID_MAX_LENGTH,
    PRODUCT_ISIN_CODE_LENGTH,
    PRODUCT_NAME_MAX_LENGTH,
    productIdValidator,
    productNameValidator,
} from '../../validators/products';
import { resolveUnit } from '../Unit';
import useSingleValidator from '../../hooks/useSingleValidator';
import {
    ENERGY,
    getProductType,
    SECURITY,
} from '../../utils/ProductTypes';
import ExpandedListTab from '../ExpandedListTab';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

ProductCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
    productProps: productPropertiesType.isRequired,
};

function ProductCreateTab(props) {
    const { hideHandler, productProps } = props;
    const {
        availableDeliveryTypes,
        availableDeliveryPeriods,
        availableQuantityUnits,
        availableProductTypes,
    } = productProps;
    const { t } = useTranslation();
    const nameState = useExtendedState('');
    const productTypeState = useExtendedState(getProductType(availableProductTypes[0]));
    const isinCodeState = useExtendedState(null);
    const productIdState = useExtendedState('');
    const iconSymbolState = useExtendedState('');
    const iconColorState = useExtendedState('#0075ff');
    const deliveryTypeState = useExtendedState(availableDeliveryTypes[0] || '');
    const deliveryPeriodState = useExtendedState(availableDeliveryPeriods[0] || '');
    const quantityUnitState = useExtendedState(availableQuantityUnits[0] || '');
    const scopeState = useExtendedState(ETS1.serverType);
    const { validators, isValid } = useMultiValidator(false, {
        name: compositeValidator(productNameValidator(t), wafValidator(t)),
        productId: compositeValidator(productIdValidator(t), wafValidator(t)),
    });
    const { validator: isinValidator, isValid: isIsinValid, reset: resetIsinValidator } = useSingleValidator(false, useMemo(() => compositeValidator(isinCodeValidator(t), wafValidator(t)), [t]));
    const keyToProductType = (key) => getProductType(key);
    const setNewProductType = () => {
        resetIsinValidator();
        isinCodeState.reset();
        deliveryTypeState.reset();
        deliveryPeriodState.reset();
        quantityUnitState.reset();
        scopeState.reset();
    };
    const [isDisabled, setIsDisabled] = useState(true);
    const isinValidityPromise = productTypeState.value.is(SECURITY) ? isIsinValid : Promise.resolve;
    Promise.all([isValid(), isinValidityPromise])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(CREATE_PRODUCT, {
        variables: {
            name: nameState.value,
            productId: productIdState.value,
            productType: productTypeState.value.serverType,
            iconSymbol: iconSymbolState.value,
            iconColor: iconColorState.value,
            isinCode: productTypeState.value.is(SECURITY) ? isinCodeState.value : null,
            deliveryType: productTypeState.value.is(ENERGY) ? deliveryTypeState.value : null,
            deliveryPeriod: productTypeState.value.is(ENERGY) ? deliveryPeriodState.value : null,
            quantityUnit: productTypeState.value.is(ENERGY) ? quantityUnitState.value : null,
            scope: productTypeState.value.is(SECURITY) ? scopeState.value : null,
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'products.notification.product-create-success', dataProperty: 'createProduct' },
        failureMessageOptions: { key: 'products.notification.product-create-failure', params: { name: nameState.value, productId: productIdState.value } },
        refetchQueries: [ALL_PRODUCTS_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="product-create-tab"
            title={t('products.product-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <TextField
                    label={t('products.name')}
                    state={nameState}
                    validator={validators.name}
                    className="product-create-tab--fields--name wide"
                    placeholder={t('products.placeholder.name')}
                    maxLength={PRODUCT_NAME_MAX_LENGTH}
                    isRequired
                />
                <TextField
                    label={t('products.product-id')}
                    state={productIdState}
                    validator={validators.productId}
                    className="product-create-tab--fields--product-id"
                    placeholder={t('products.placeholder.product-id')}
                    maxLength={PRODUCT_ID_MAX_LENGTH}
                    isRequired
                />
                {availableProductTypes.length > 1 && (
                    <SelectField
                        label={t('products.product-type')}
                        value={productTypeState.value.serverType}
                        state={productTypeState}
                        onChange={setNewProductType}
                        valueTransformer={keyToProductType}
                        className="product-create-tab--fields--product-type"
                        inputId="product-create-tab--fields--product-type"
                        options={availableProductTypes.map((availableProductType) => (
                            <option value={availableProductType} key={availableProductType}>
                                {getProductType(availableProductType).translate(t)}
                            </option>
                        ))}
                        isRequired
                    />
                )}
                {productTypeState.value.is(SECURITY) && (
                    <SelectField
                        label={t('products.scope')}
                        state={scopeState}
                        className="product-create-tab--fields--scope"
                        inputId="product-create-tab--fields--scope"
                        options={getAllScopes().map((scope) => <option value={scope.serverType} key={scope.serverType}>{scope.translate(t)}</option>)}
                        isRequired
                    />
                )}
                <IconField
                    label={t('products.icon-symbol')}
                    state={iconSymbolState}
                    inputId="product-create-tab--fields--icon-symbol"
                    className="product-create-tab--fields--icon-symbol"
                />
                <ColorField
                    label={t('products.icon-color')}
                    state={iconColorState}
                    inputId="product-create-tab--fields--icon-color"
                    className="product-create-tab--fields--icon-color"
                />
                {productTypeState.value.is(SECURITY) && (
                    <TextField
                        label={t('products.isin-code')}
                        state={isinCodeState}
                        validator={isinValidator}
                        className="product-create-tab--fields--isin-code wide"
                        placeholder={t('products.placeholder.isin-code')}
                        maxLength={PRODUCT_ISIN_CODE_LENGTH}
                        isRequired
                    />
                )}
                {productTypeState.value.is(ENERGY) && (
                    <>
                        <SelectField
                            label={t('products.delivery-period')}
                            state={deliveryPeriodState}
                            className="product-create-tab--fields--delivery-period"
                            inputId="product-create-tab--fields--delivery-period"
                            options={availableDeliveryPeriods.map((period) => <option value={period} key={period}>{period}</option>)}
                            isRequired
                        />
                        <SelectField
                            label={t('products.quantity-unit')}
                            state={quantityUnitState}
                            className="product-create-tab--fields--quantity-unit"
                            inputId="product-create-tab--fields--quantity-unit"
                            options={availableQuantityUnits.map((unit) => <option value={unit} key={unit}>{resolveUnit(unit, t)}</option>)}
                            isRequired
                        />
                        <SelectField
                            label={t('products.delivery-type')}
                            state={deliveryTypeState}
                            className="product-create-tab--fields--delivery-type"
                            inputId="product-create-tab--fields--delivery-type"
                            options={availableDeliveryTypes.map((type) => <option value={type} key={type}>{type}</option>)}
                            isRequired
                        />
                    </>
                )}
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton disabled={isDisabled} extendedMutation={extendedMutation} confirmationMessage={t('products.confirm-create-product', { name: nameState.value, productId: productIdState.value })} />
            </div>
        </ExpandedListTab>
    );
}

export default ProductCreateTab;
